import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import { InlineFileUpload } from '@/shared/components/file-upload/InlineFileUpload';
import { Images } from 'lucide-react';
import { VideoInputToggle } from '../media/VideoInputToggle';
import { MediaGalleryModal } from '../media/MediaGalleryModal';
import { useMediaGallery, type MediaItem } from '../hooks/useMediaGallery';
import type { ProviderEditorFormData } from '../schemas';

type ProfileMediaSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
  initialGalleryItems?: Array<{
    id: string;
    mediaType: 'image' | 'video';
    mediaUrl: string;
    caption?: string;
    isPrimary: boolean;
    displayOrder: number;
  }>;
};

type MediaGalleryControllerProps = {
  value: MediaItem[];
  onChange: (items: MediaItem[]) => void;
};

/**
 * MediaGalleryController - Wrapper component to prevent setState during render
 * 
 * This component is necessary to avoid the React error:
 * "Cannot update a component while rendering a different component"
 * 
 * By moving the useMediaGallery hook outside of Controller's render function,
 * we prevent onChange from being called during the render phase.
 */
function MediaGalleryController({ value, onChange }: MediaGalleryControllerProps) {
  // Create gallery hook outside of render
  const gallery = useMediaGallery({
    initialItems: value,
  });

  // Sync gallery changes to form (outside of render)
  useEffect(() => {
    onChange(gallery.items);
  }, [gallery.items, onChange]);

  return (
    <>
      {/* Manage Gallery Button */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium text-gray-900">Photo Gallery</h4>
            <p className="mt-1 text-sm text-gray-600">
              Manage additional photos, captions, display order, and primary image selection
            </p>
            {gallery.items.length > 0 && (
              <p className="mt-2 text-sm text-gray-700">
                <span className="font-medium">{gallery.items.length}</span>{' '}
                {gallery.items.length === 1 ? 'item' : 'items'} in gallery
                {gallery.primaryItem && (
                  <span className="text-gray-500">
                    {' '}
                    • Primary: {gallery.primaryItem.caption || `Item ${gallery.primaryItem.displayOrder + 1}`}
                  </span>
                )}
              </p>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={gallery.openModal}>
            <Images className="mr-2 h-4 w-4" />
            Manage Gallery ({gallery.items.length})
          </Button>
        </div>
      </div>

      {/* Media Gallery Modal */}
      <MediaGalleryModal
        isOpen={gallery.isModalOpen}
        onClose={gallery.closeModal}
        items={gallery.items}
        onItemsChange={gallery.reset}
        onAddItem={gallery.addItem}
        onRemoveItem={gallery.removeItem}
        onReorder={gallery.reorderItems}
        onSetPrimary={gallery.setPrimary}
        onCaptionChange={gallery.updateCaption}
      />
    </>
  );
}

/**
 * ProfileMediaSection - Manages provider media assets
 * 
 * Features:
 * - Profile photo upload (avatar) - 5MB limit
 * - Hero media upload (banner image) - 5MB limit
 * - Introduction video (dual-mode: upload OR external URL) - 100MB limit for uploads
 * - Photo gallery management with drag-drop reordering
 * 
 * Media upload flow:
 * 1. Inline uploads for profile photo and hero media (simple file picker)
 * 2. Dual-mode video input (upload to S3 OR provide external URL)
 * 3. Gallery modal for managing multiple photos with drag-drop reordering
 * 
 * Technical notes:
 * - Uses InlineFileUpload for profile_photo and hero_media_url
 * - Uses VideoInputToggle for introduction_video_url
 * - Uses MediaGalleryModal for provider_media relationship
 * - Uses useMediaGallery hook for gallery state management
 * - All uploads use S3 pre-signed URLs via FileUploadService
 */
export function ProfileMediaSection({
  control,
  errors,
}: ProfileMediaSectionProps) {
  return (
    <div className="space-y-6">
      {/* Profile Photo */}
      <div>
        <Controller
          name="user.profilePhoto"
          control={control}
          render={({ field }) => (
            <InlineFileUpload
              label="Profile Photo"
              value={field.value || undefined}
              onChange={field.onChange}
              accept="image/*"
              maxSizeMB={5}
              hint="Square avatar image recommended (5MB max)"
              error={errors.user?.profilePhoto?.message}
              previewClassName="h-32 w-32 rounded-full"
            />
          )}
        />
      </div>

      {/* Hero Media (Banner) */}
      <div>
        <Controller
          name="provider.heroMediaUrl"
          control={control}
          render={({ field }) => (
            <InlineFileUpload
              label="Hero Banner Image"
              value={field.value || undefined}
              onChange={field.onChange}
              accept="image/*"
              maxSizeMB={5}
              hint="Wide banner image for profile header (1200x400px recommended, 5MB max)"
              error={errors.provider?.heroMediaUrl?.message}
              previewClassName="h-32"
            />
          )}
        />
      </div>

      {/* Introduction Video */}
      <div>
        <Controller
          name="provider.introductionVideoUrl"
          control={control}
          render={({ field }) => (
            <VideoInputToggle
              label="Introduction Video"
              value={field.value || undefined}
              onChange={field.onChange}
              maxSizeMB={100}
              hint="Upload video file (100MB max) or provide external URL"
              error={errors.provider?.introductionVideoUrl?.message}
            />
          )}
        />
      </div>

      {/* Media Gallery - Controlled by React Hook Form */}
      <Controller
        name="mediaGallery"
        control={control}
        render={({ field }) => (
          <MediaGalleryController
            value={field.value || []}
            onChange={field.onChange}
          />
        )}
      />
    </div>
  );
}


